import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Tabs } from "@shared/ui/tabs";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { ChevronLeft, Edit, Trash2, HelpCircle, Box, ArrowUpLeft, ArrowLeft } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import OverviewTab from "./tabs/Overview";
import CustomFieldsTab from "./tabs/CustomFields";
import { ResourceSubtypeForm } from "../create/create";

export interface CustomField {
  id: number;
  label: string;
  type: string;
  required: boolean;
  placeholder: string;
}

export interface SubtypeDetail {
  id: string;
  name: string;
  code: string;
  resourceType: string;
  description: string;
  created: string;
  createdBy: string;
  status: string;
  activities: string[];
  capabilities: {
    cycles: boolean;
    routes: boolean;
    validation: boolean;
    inventory: boolean;
    svcOrders: boolean;
  };
  customFields: CustomField[];
}

const mockSubtypeDetails: Record<string, SubtypeDetail> = {
  "1": {
    id: "1",
    name: "Analog dial meter",
    code: "ANLG-MTR",
    resourceType: "Water meter",
    description: "Standard mechanical analog dial utility meter",
    created: "2026-05-12",
    createdBy: "John Doe",
    status: "Active",
    activities: ["Read", "Maintenance"],
    capabilities: {
      cycles: true,
      routes: true,
      validation: false,
      inventory: true,
      svcOrders: true,
    },
    customFields: [
      { id: 1, label: "Serial Number", type: "Text", required: true, placeholder: "e.g. SN-001234" },
      { id: 2, label: "Dial Units", type: "Dropdown", required: true, placeholder: "kWh, m³, gallons" },
      { id: 3, label: "Installation Date", type: "Date", required: false, placeholder: "—" },
    ],
  },
  "2": {
    id: "2",
    name: "Digital smart meter",
    code: "DGTL-MTR",
    resourceType: "Electricity meter",
    description: "Electronic smart grid energy meter",
    created: "2026-06-20",
    createdBy: "Jane Smith",
    status: "Active",
    activities: ["Read", "Collection", "Inspection"],
    capabilities: {
      cycles: true,
      routes: true,
      validation: true,
      inventory: false,
      svcOrders: true,
    },
    customFields: [
      { id: 1, label: "Signal Strength", type: "Number", required: true, placeholder: "e.g. dbm value" },
      { id: 2, label: "Firmware Version", type: "Text", required: false, placeholder: "e.g. v1.2.3" },
    ],
  },
  "3": {
    id: "3",
    name: "Rotary gas meter",
    code: "RTRY-GAS",
    resourceType: "Gas meter",
    description: "Heavy duty rotary displacement gas meter",
    created: "2026-07-01",
    createdBy: "Bob Johnson",
    status: "Active",
    activities: ["Read", "Inspection"],
    capabilities: {
      cycles: true,
      routes: true,
      validation: true,
      inventory: true,
      svcOrders: true,
    },
    customFields: [
      { id: 1, label: "Pressure Read", type: "Number", required: true, placeholder: "e.g. psi level" },
      { id: 2, label: "Flow Rate", type: "Number", required: true, placeholder: "e.g. cfh" },
    ],
  },
};

export default function ResourceSubtypeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const detail = mockSubtypeDetails[id || "1"] || mockSubtypeDetails["1"];

  const handleEdit = () => {
    setIsFormOpen(true);
  };

  const handleDelete = () => {
    console.log("Deleting subtype:", detail.id);
  };

  const tabComponents = {
    overview: {
      label: "Overview",
      shortLabel: "Overview",
      component: <OverviewTab detail={detail} />,
    },
    customFields: {
      label: `Custom Fields (${detail.customFields.length})`,
      shortLabel: "Custom Fields",
      component: <CustomFieldsTab detail={detail} />,
    },
  };

  const urlMapping = {
    overview: "overview",
    customFields: "customFields",
  };

  return (
    <div className="p-3 space-y-6">
      {/* Page Header with Back Navigation */}
      <PageHeader
        title={'Sub Type Detail'}
        subtitle="View details and inherited capabilities of this resource sub type."
        icon={ArrowLeft}
        onIconClick={() => navigate("/mx/settings/meterread-format?tab=subtype")}
      />

      {/* Top Cards Info Area */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Resource Main Info Card */}
        <Card className="lg:col-span-3 border-gray-200 shadow-sm">
          <CardContent className="flex items-center gap-4">
            <div className="p-4 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
              <Box className="w-8 h-8" />
            </div>
            <div className="space-y-1.5">
              <h2 className="text-xl font-semibold text-gray-900">{detail.name}</h2>
              <p className="text-sm text-gray-500">Code: {detail.code}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge variant="outline" className="text-gray-600 bg-gray-50 border-gray-200">
                  {detail.code}
                </Badge>
                <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-100">
                  {detail.resourceType}
                </Badge>
                <Badge variant="outline" className="text-indigo-600 bg-indigo-50 border-indigo-100">
                  {detail.customFields.length} fields
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Card */}
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-xs font-semibold text-gray-500 tracking-wider">
                Quick Actions
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent>Actions for this resource sub type</TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2 border-gray-200 text-gray-700 font-medium"
                onClick={handleEdit}
              >
                <Edit className="w-4 h-4" />
                Edit Resource
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium"
                onClick={handleDelete}
              >
                <Trash2 className="w-4 h-4" />
                Delete Resource
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overview Fields Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Overview Box */}
        <Card className="lg:col-span-3 border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">
              Resource Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <ValueLabel label="Name" value={detail.name} />
              <ValueLabel label="Code" value={detail.code} />
              <ValueLabel
                label="Resource Type"
                value={
                  <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-100">
                    {detail.resourceType}
                  </Badge>
                }
              />
              <ValueLabel label="Created" value={detail.created.replace(/-/g, "/")} />
            </div>
          </CardContent>
        </Card>

        {/* Description Box */}
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">
              Description
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {detail.description || "No description provided."}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs System (Using Shared Tabs Component) */}
      <Tabs
        defaultValue="overview"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        idPrefix="subtype-detail-tab"
        tabsListFullWidth={true}
        tabsListClassName="grid w-full grid-cols-2 mb-6"
        level={1}
      />

      {/* Edit Form Modal */}
      <ResourceSubtypeForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        editData={detail}
        onSuccess={(updatedData) => {
          console.log("Updated subtype details successfully:", updatedData);
        }}
      />
    </div>
  );
}
